"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useDebounceValue } from "usehooks-ts";
import { formatDistanceToNow } from "date-fns";
import { listProjects } from "@/lib/api/generated";
import { useSelectedProject } from "@/hooks/use-search";
import { useAuth } from "@/features/auth";
import { useCreateProjectModal } from "@/features/projects";
import {
  useProjectSwitch,
  useProjectSwitchStore,
} from "@/features/projects/hooks/use-project-switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { coverForId } from "@/lib/image/covers";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { InputVariantProps } from "@/components/ui/input";

const PER_PAGE = 20;

export type ProjectSelectOption = {
  id: string;
  title: string;
  cover_image_url?: string | null;
};

export interface ProjectSelectProps {
  /**
   * Controlled selected project id. When provided with `onValueChange`,
   * selection is local (does not switch the navbar/global project).
   */
  value?: string | null;
  onValueChange?: (projectId: string, project: ProjectSelectOption) => void;
  /** Display info for the controlled value before/without the dropdown list. */
  displayProject?: ProjectSelectOption | null;
  size?: NonNullable<InputVariantProps["size"]>;
  variant?: NonNullable<InputVariantProps["variant"]>;
  className?: string;
  contentClassName?: string;
  align?: "start" | "center" | "end";
  matchTriggerWidth?: boolean;
  showCreate?: boolean;
  /** When true, the trigger stays visible but cannot open or change selection. */
  disabled?: boolean;
  /**
   * `navbar` — compact muted pill (default).
   * `sidebar` — full-width ghost row for the sidebar header: cover tile +
   * title + grabber; `collapsed` reduces it to the tile alone.
   */
  appearance?: "navbar" | "sidebar";
  collapsed?: boolean;
}

export function ProjectSelect({
  value,
  onValueChange,
  displayProject: displayProjectProp,
  size = "sm",
  variant = "muted",
  className,
  contentClassName,
  align = "end",
  matchTriggerWidth = false,
  showCreate = true,
  disabled = false,
  appearance = "navbar",
  collapsed = false,
}: ProjectSelectProps = {}) {
  const isSidebar = appearance === "sidebar";
  const router = useRouter();
  const { openCreateProject } = useCreateProjectModal();
  const [open, setOpen] = useState(false);
  const isControlled = onValueChange != null;

  const { selectedProjectId: authProjectId } = useAuth();
  const { data: selectedProject, isLoading: projectLoading } =
    useSelectedProject();
  const pendingProject = useProjectSwitchStore((s) => s.pendingProject);
  const globalDisplayProject = pendingProject ?? selectedProject;
  const selectedProjectId = isControlled
    ? (value ?? null)
    : (selectedProject?.id ?? null);
  const isProjectPending =
    !isControlled && !!authProjectId && !globalDisplayProject && projectLoading;

  const { switchProject, isPending: isSelectPending } = useProjectSwitch({
    onSuccess: () => {
      router.push("/jobs");
    },
  });

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["projects", "navbar", debouncedSearch],
      queryFn: ({ pageParam = 1 }) =>
        listProjects({
          search: debouncedSearch || undefined,
          page: pageParam as number,
          per_page: PER_PAGE,
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, p) => n + p.projects.length, 0);
        return loaded < lastPage.total ? allPages.length + 1 : undefined;
      },
      enabled: open,
    });

  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset local search state on dropdown open
    setSearch("");
    const id = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [open]);

  const projects = useMemo(
    () => data?.pages.flatMap((p) => p.projects) ?? [],
    [data],
  );
  const total = data?.pages[0]?.total ?? 0;
  const remaining = Math.max(0, total - projects.length);

  const listMatch = useMemo(() => {
    if (!selectedProjectId) return null;
    return projects.find((p) => p.id === selectedProjectId) ?? null;
  }, [projects, selectedProjectId]);

  const displayProject = isControlled
    ? (displayProjectProp ?? listMatch)
    : globalDisplayProject;

  const handleSelect = (project: ProjectSelectOption) => {
    if (project.id === selectedProjectId) return;
    if (isControlled) {
      setOpen(false);
      onValueChange(project.id, project);
      return;
    }
    if (isSelectPending) return;
    setOpen(false);
    useProjectSwitchStore.setState({ showLoader: true });
    switchProject(project.id, {
      id: project.id,
      title: project.title,
      cover_image_url: project.cover_image_url ?? null,
    });
  };

  return (
    <Select.Root
      open={disabled ? false : open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
      }}
    >
      {isSidebar ? (
        <Select.Trigger
          size="none"
          variant="ghost"
          disabled={disabled}
          aria-label={
            displayProject
              ? `Project: ${displayProject.title}. Switch project`
              : "Select project"
          }
          className={cn(
            "group/workspace min-w-0 cursor-pointer rounded-md",
            "transition-[background-color,box-shadow] duration-150",
            "outline-none focus-visible:ring-2 focus-visible:ring-ai/40",
            collapsed
              ? "h-9 w-9 justify-center px-0 hover:bg-contrast/4 data-[state=open]:bg-contrast/6"
              : cn(
                  // Boxed like the Quick find field directly beneath it.
                  "h-8 w-full gap-2 px-2",
                  "bg-contrast/[0.03] shadow-[inset_0_0_0_1px_var(--border-alpha)]",
                  "hover:bg-contrast/[0.06] hover:shadow-[inset_0_0_0_1px_var(--border-strong-alpha,var(--border-alpha))]",
                  "data-[state=open]:bg-contrast/[0.06]",
                ),
            className,
          )}
        >
          {displayProject ? (
            <Avatar className="size-5 shrink-0 rounded-full shadow-[inset_0_0_0_1px_var(--border-alpha)]">
              <AvatarImage
                src={displayProject.cover_image_url ?? undefined}
                alt=""
              />
              <AvatarFallback className="rounded-full p-0 bg-transparent">
                <img
                  src={coverForId(displayProject.id)}
                  alt=""
                  className="size-full object-cover"
                />
              </AvatarFallback>
            </Avatar>
          ) : (
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-contrast/10 shadow-[inset_0_0_0_1px_var(--border-alpha)]">
              <Icon
                name="IconFolderBookmarks"
                size={12}
                className="text-heading opacity-40"
              />
            </span>
          )}
          {!collapsed && (
            <>
              {isProjectPending ? (
                <span className="h-3.5 flex-1 max-w-24 shimmer shimmer-bg rounded bg-contrast/10" />
              ) : (
                <span className="min-w-0 flex-1 truncate text-left text-[13px] font-semibold leading-5 text-heading">
                  {displayProject?.title || "Select project"}
                </span>
              )}
              <Select.Icon
                size={14}
                className="ml-0 text-contrast/35 transition-colors group-hover/workspace:text-contrast/60"
              />
            </>
          )}
        </Select.Trigger>
      ) : (
        <Select.Trigger
          size={size}
          variant={variant}
          disabled={disabled}
          className={cn(
            isControlled ? "w-full px-3" : "max-w-52 w-auto px-2 flex-auto",
            className,
          )}
        >
          {displayProject ? (
            <Avatar className="size-4 shrink-0">
              <AvatarImage
                src={displayProject.cover_image_url ?? undefined}
                alt=""
              />
              <AvatarFallback className="p-0 bg-transparent">
                <img
                  src={coverForId(displayProject.id)}
                  alt=""
                  className="size-full object-cover"
                />
              </AvatarFallback>
            </Avatar>
          ) : (
            <div className="size-4 rounded-full shrink-0 bg-contrast/10" />
          )}
          {isProjectPending ? (
            <div className="h-3.5 w-20 shimmer shimmer-bg text-foreground rounded bg-contrast/10" />
          ) : (
            <span className="truncate">
              {displayProject?.title || "Select Project"}
            </span>
          )}
          <Select.Icon className="ml-auto" />
        </Select.Trigger>
      )}

      <Select.Content
        align={isSidebar ? "start" : align}
        sideOffset={isSidebar ? 6 : undefined}
        matchTriggerWidth={matchTriggerWidth}
        className={cn(
          matchTriggerWidth ? undefined : isSidebar ? "w-[300px]" : "w-[280px]",
          contentClassName,
        )}
      >
        <Select.Search
          ref={inputRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search projects"
        />
        {isLoading && !data ? (
          <Select.ListPending />
        ) : projects.length === 0 && !search ? (
          <Select.Empty>
            <Icon
              name="IconFolderBookmarks"
              size={32}
              className="text-heading opacity-40"
            />
            <Select.EmptyTitle>No projects</Select.EmptyTitle>
            <Select.EmptyDescription>
              Easily switch between available projects by selecting them from
              here.{" "}
              <span className="text-[#437dfc] cursor-pointer">
                Learn more about projects
              </span>
            </Select.EmptyDescription>
          </Select.Empty>
        ) : projects.length === 0 ? (
          <Select.Empty>
            <Icon
              name="IconMagnifyingGlass"
              size={32}
              className="text-heading opacity-40"
            />
            <Select.EmptyTitle>No results</Select.EmptyTitle>
            <Select.EmptyDescription>
              No projects match &ldquo;{search}&rdquo;
            </Select.EmptyDescription>
          </Select.Empty>
        ) : (
          <Select.List className="max-h-80">
            {projects.map((p) => {
              const isActive = p.id === selectedProjectId;
              const jobCount = p.job_count ?? 0;
              const meta = [
                `${jobCount} ${jobCount === 1 ? "job" : "jobs"}`,
                `updated ${formatDistanceToNow(new Date(p.updated_at), { addSuffix: true })}`,
              ].join(" · ");
              return (
                <Select.Item
                  key={p.id}
                  selected={isActive}
                  disabled={
                    isActive || (!isControlled && isSelectPending)
                  }
                  className={cn(
                    "h-auto gap-2.5 px-2 py-1.5",
                    isActive && "disabled:opacity-100",
                  )}
                  onClick={() => {
                    if (!isActive && (isControlled || !isSelectPending)) {
                      handleSelect({
                        id: p.id,
                        title: p.title,
                        cover_image_url: p.cover_image_url ?? null,
                      });
                    }
                  }}
                >
                  <Avatar className="size-8 shrink-0 rounded-full border border-contrast/8">
                    <AvatarImage src={p.cover_image_url ?? undefined} alt="" />
                    <AvatarFallback className="p-0 bg-transparent">
                      <img
                        src={coverForId(p.id)}
                        alt=""
                        className="size-full object-cover"
                      />
                    </AvatarFallback>
                  </Avatar>
                  <span className="flex min-w-0 flex-1 flex-col items-start gap-px text-left">
                    <span
                      className={cn(
                        "w-full truncate text-sm leading-5",
                        isActive ? "font-medium text-heading" : "text-heading",
                      )}
                    >
                      {p.title}
                    </span>
                    <span className="w-full truncate text-[11px] leading-4 text-contrast/40">
                      {meta}
                    </span>
                  </span>
                  {isActive && (
                    <Icon
                      name="IconSubscriptionTick1"
                      size={16}
                      className="text-ai shrink-0"
                    />
                  )}
                </Select.Item>
              );
            })}
            {hasNextPage && (
              <Select.ShowMore
                remaining={remaining}
                disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              />
            )}
          </Select.List>
        )}

        {/* Controlled/clone pickers pass showCreate={false} so create/nav
            actions don't appear mid-flow. Navbar keeps the full footer. */}
        {showCreate ? (
          <Select.Footer className="justify-between">
            <button
              type="button"
              className="text-xs text-contrast/50 hover:text-contrast/80 transition-colors cursor-pointer"
              onClick={() => {
                setOpen(false);
                router.push("/projects");
              }}
            >
              View all projects
            </button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="flex items-center gap-1"
              onClick={() => {
                setOpen(false);
                openCreateProject();
              }}
            >
              <Icon name="IconPlusMedium" />
              Create new
            </Button>
          </Select.Footer>
        ) : null}
      </Select.Content>
    </Select.Root>
  );
}
